import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  VStack,
  HStack,
  Grid,
  GridItem,
  Progress,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Spacer,
  Icon,
  Divider
} from '@chakra-ui/react';
import {
  FaPlay,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaCalendarAlt,
  FaTrophy,
  FaGraduationCap
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const StudentQCMPage = () => {
  const { user } = useContext(AuthContext);
  const [qcms, setQcms] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQcm, setSelectedQcm] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  useEffect(() => {
    fetchStudentQCMs();
    fetchMyAttempts();
  }, []);

  const fetchStudentQCMs = async () => {
    try {
      // Get QCMs for all enrolled courses
      const enrolledCourses = user.enrolledCourses || [];
      const allQcms = [];

      for (const enrollment of enrolledCourses) {
        try {
          const response = await axiosInstance.get(`/api/qcm/tests/course/${enrollment.course}`);
          allQcms.push(...response.data);
        } catch (error) {
          console.error(`Error fetching QCMs for course ${enrollment.course}:`, error);
        }
      }

      setQcms(allQcms);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل الاختبارات',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAttempts = async () => {
    try {
      const response = await axiosInstance.get('/api/qcm/attempts/my');
      setAttempts(response.data);
    } catch (error) {
      console.error('Error fetching attempts:', error);
    }
  };

  const getAttemptForQcm = (qcmId) => {
    return attempts.filter(attempt => attempt.test._id === qcmId);
  };

  const getBestScore = (qcmId) => {
    const qcmAttempts = getAttemptForQcm(qcmId);
    if (qcmAttempts.length === 0) return null;
    return Math.max(...qcmAttempts.map(attempt => attempt.score));
  };

  const getAttemptCount = (qcmId) => {
    return getAttemptForQcm(qcmId).length;
  };

  const canTakeQcm = (qcm) => {
    const assignment = qcm.assignedStudents?.find(a => a.student === user._id);
    if (!assignment) return false;
    
    const attemptCount = getAttemptCount(qcm._id);
    return attemptCount < assignment.maxAttempts;
  };

  const getQcmStatus = (qcm) => {
    const attemptCount = getAttemptCount(qcm._id);
    const assignment = qcm.assignedStudents?.find(a => a.student === user._id);
    
    if (!assignment) return 'غير مخصص';
    if (attemptCount === 0) return 'لم يبدأ';
    if (attemptCount >= assignment.maxAttempts) return 'مكتمل';
    return 'في التقدم';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'لم يبدأ': return 'blue';
      case 'في التقدم': return 'orange';
      case 'مكتمل': return 'green';
      case 'غير مخصص': return 'gray';
      default: return 'gray';
    }
  };

  const handleStartQcm = (qcm) => {
    if (!canTakeQcm(qcm)) {
      toast({
        title: 'تنبيه',
        description: 'لقد استنفدت عدد المحاولات المسموحة لهذا الاختبار',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    navigate(`/qcm/take/${qcm._id}`);
  };

  const handleViewResults = (qcm) => {
    const qcmAttempts = getAttemptForQcm(qcm._id);
    if (qcmAttempts.length > 0) {
      navigate(`/qcm/results/${qcmAttempts[0]._id}`);
    }
  };

  const openQcmDetails = (qcm) => {
    setSelectedQcm(qcm);
    onOpen();
  };

  if (loading) {
    return (
      <Box dir="rtl">
        <Header />
        <Container maxW="8xl" py={8}>
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color={primaryColor} />
          </Flex>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      <Header />
      <Container maxW="8xl" py={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Heading size="lg" mb={6} color={secondaryColor} fontFamily="Tajawal, sans-serif">
            اختباراتي
          </Heading>

          {qcms.length === 0 ? (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Text fontFamily="Tajawal, sans-serif">لا توجد اختبارات مخصصة لك حالياً</Text>
            </Alert>
          ) : (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
              {qcms.map((qcm) => {
                const status = getQcmStatus(qcm);
                const bestScore = getBestScore(qcm._id);
                const attemptCount = getAttemptCount(qcm._id);
                const assignment = qcm.assignedStudents?.find(a => a.student === user._id);

                return (
                  <GridItem key={qcm._id}>
                    <motion.div
                      whileHover={{ y: -5 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card 
                        bg="white" 
                        boxShadow="md" 
                        borderRadius="lg"
                        _hover={{ boxShadow: 'xl' }}
                        transition="all 0.3s"
                        h="100%"
                      >
                        <CardHeader pb={2}>
                          <Flex align="center" mb={2}>
                            <Heading size="md" color={secondaryColor} fontFamily="Tajawal, sans-serif">
                              {qcm.title}
                            </Heading>
                            <Spacer />
                            <Badge colorScheme={getStatusColor(status)} fontFamily="Tajawal, sans-serif">
                              {status}
                            </Badge>
                          </Flex>
                          <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                            {qcm.course?.title}
                          </Text>
                        </CardHeader>

                        <CardBody pt={0}>
                          <VStack align="stretch" spacing={3}>
                            {qcm.description && (
                              <Text fontSize="sm" color="gray.700" fontFamily="Tajawal, sans-serif">
                                {qcm.description}
                              </Text>
                            )}

                            <HStack justify="space-between">
                              <HStack>
                                <Icon as={FaGraduationCap} color={primaryColor} />
                                <Text fontSize="sm" fontFamily="Tajawal, sans-serif">
                                  {qcm.questions?.length || 0} سؤال
                                </Text>
                              </HStack>
                              {qcm.timeLimit && (
                                <HStack>
                                  <Icon as={FaClock} color={primaryColor} />
                                  <Text fontSize="sm" fontFamily="Tajawal, sans-serif">
                                    {qcm.timeLimit} دقيقة
                                  </Text>
                                </HStack>
                              )}
                            </HStack>

                            {assignment && (
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                                  المحاولات: {attemptCount}/{assignment.maxAttempts}
                                </Text>
                                {assignment.dueDate && (
                                  <HStack>
                                    <Icon as={FaCalendarAlt} color="red.500" />
                                    <Text fontSize="sm" color="red.500" fontFamily="Tajawal, sans-serif">
                                      {new Date(assignment.dueDate).toLocaleDateString('ar-EG')}
                                    </Text>
                                  </HStack>
                                )}
                              </HStack>
                            )}

                            {bestScore !== null && (
                              <HStack>
                                <Icon as={FaTrophy} color={primaryColor} />
                                <Text fontSize="sm" fontFamily="Tajawal, sans-serif">
                                  أفضل نتيجة: {Math.round(bestScore)}%
                                </Text>
                                {qcm.passingScore && (
                                  <Badge 
                                    colorScheme={bestScore >= qcm.passingScore ? 'green' : 'red'}
                                    fontFamily="Tajawal, sans-serif"
                                  >
                                    {bestScore >= qcm.passingScore ? 'نجح' : 'راسب'}
                                  </Badge>
                                )}
                              </HStack>
                            )}

                            <Divider />

                            <VStack spacing={2}>
                              <Button
                                colorScheme="yellow"
                                bg={primaryColor}
                                color={secondaryColor}
                                size="sm"
                                leftIcon={<FaPlay />}
                                onClick={() => handleStartQcm(qcm)}
                                isDisabled={!canTakeQcm(qcm)}
                                w="100%"
                                fontFamily="Tajawal, sans-serif"
                              >
                                {attemptCount === 0 ? 'بدء الاختبار' : 'محاولة جديدة'}
                              </Button>

                              <HStack w="100%">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  leftIcon={<FaEye />}
                                  onClick={() => openQcmDetails(qcm)}
                                  flex={1}
                                  fontFamily="Tajawal, sans-serif"
                                >
                                  التفاصيل
                                </Button>
                                {attemptCount > 0 && (
                                  <Button
                                    variant="outline"
                                    colorScheme="green"
                                    size="sm"
                                    leftIcon={<FaCheckCircle />}
                                    onClick={() => handleViewResults(qcm)}
                                    flex={1}
                                    fontFamily="Tajawal, sans-serif"
                                  >
                                    النتائج
                                  </Button>
                                )}
                              </HStack>
                            </VStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    </motion.div>
                  </GridItem>
                );
              })}
            </Grid>
          )}
        </motion.div>

        {/* QCM Details Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="Tajawal, sans-serif">
              تفاصيل الاختبار
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedQcm && (
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Heading size="md" mb={2} fontFamily="Tajawal, sans-serif">
                      {selectedQcm.title}
                    </Heading>
                    <Text color="gray.600" fontFamily="Tajawal, sans-serif">
                      {selectedQcm.course?.title}
                    </Text>
                  </Box>

                  {selectedQcm.description && (
                    <Box>
                      <Text fontWeight="bold" mb={1} fontFamily="Tajawal, sans-serif">الوصف:</Text>
                      <Text fontFamily="Tajawal, sans-serif">{selectedQcm.description}</Text>
                    </Box>
                  )}

                  {selectedQcm.instructions && (
                    <Box>
                      <Text fontWeight="bold" mb={1} fontFamily="Tajawal, sans-serif">التعليمات:</Text>
                      <Text fontFamily="Tajawal, sans-serif">{selectedQcm.instructions}</Text>
                    </Box>
                  )}

                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <Box>
                      <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">عدد الأسئلة:</Text>
                      <Text fontFamily="Tajawal, sans-serif">{selectedQcm.questions?.length || 0}</Text>
                    </Box>
                    {selectedQcm.timeLimit && (
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">الوقت المحدد:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.timeLimit} دقيقة</Text>
                      </Box>
                    )}
                    {selectedQcm.passingScore && (
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">درجة النجاح:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.passingScore}%</Text>
                      </Box>
                    )}
                    <Box>
                      <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">المحاولات المسموحة:</Text>
                      <Text fontFamily="Tajawal, sans-serif">
                        {selectedQcm.assignedStudents?.find(a => a.student === user._id)?.maxAttempts || 1}
                      </Text>
                    </Box>
                  </Grid>

                  <Button
                    colorScheme="yellow"
                    bg={primaryColor}
                    color={secondaryColor}
                    onClick={() => {
                      onClose();
                      handleStartQcm(selectedQcm);
                    }}
                    isDisabled={!canTakeQcm(selectedQcm)}
                    fontFamily="Tajawal, sans-serif"
                  >
                    بدء الاختبار
                  </Button>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
      <Footer />
    </Box>
  );
};

export default StudentQCMPage;


